import React, { useState, useEffect } from "react";
import ClientBackground from "./ClientBackground";
import { ChevronLeft } from "lucide-react";
import { useNavigate, useParams } from "react-router-dom";
import api from "../api";
import LoadingSpinner from "./LoadingSpinner";

export default function TufyLanding() {
  const navigate = useNavigate();
  const { childId } = useParams();
  const [tufyEnabled, setTufyEnabled] = useState(false);
  const [loading, setLoading] = useState(true);
  const [isUpdating, setIsUpdating] = useState(false);

  useEffect(() => {
    const fetchChildProfile = async () => {
      try {
        const response = await api.get(`/children/profiles/${childId}/`);
        setTufyEnabled(response.data.tufy_page);
      } catch (error) {
        console.error("Error fetching child profile:", error);
      } finally {
        setLoading(false);
      }
    };

    if (childId) {
      fetchChildProfile();
    } else {
      setLoading(false);
    }
  }, [childId]);

  const handleChatClick = async () => {
    if (tufyEnabled) {
      navigate(`/tufy-chat-center/${childId}`);
      return;
    }

    setIsUpdating(true);
    try {
      await api.post(`/children/profiles/${childId}/update_tufy_page/`);
      navigate(`/tufy-chat-center/${childId}`);
    } catch (error) {
      console.error("Error enabling tufy page:", error);
    } finally {
      setIsUpdating(false);
    }
  };

  if (loading) {
    return <LoadingSpinner fullScreen />;
  }

  return (
    <div className="relative min-h-screen">
      <ClientBackground />
      <div className="relative z-10">
        {/* Header */}
        <div className="bg-white/0 backdrop-blur-sm px-5 py-4 flex items-center justify-between">
          <button onClick={() => navigate(-1)} className="p-2">
            <ChevronLeft className="w-6 h-6 text-gray-800" />
          </button>
          <h1 className="text-lg font-semibold text-[#1e3a5f]">Ask Tufy</h1>
          <div className="w-10" />
        </div>

        {/* Main Content */}
        <div className="px-5 py-8 flex items-center justify-center min-h-[calc(100vh-140px)]">
          <div className="w-full max-w-md mx-auto">
            <div className="rounded-2xl p-8 md:p-10">
              {/* Tufy Image */}
              <div className="flex justify-center mb-6">
                <img
                  src="/tufy.png"
                  alt="Tufy Robot"
                  className="w-72 h-72 md:w-96 md:h-96 object-contain drop-shadow-lg"
                />
              </div>

              {/* Title */}
              <h2 className="text-2xl md:text-3xl font-bold text-[#1e3a5f] text-center mb-4">
                Hi, My Name is Tufy
              </h2>

              {/* Description */}
              <div className="space-y-4 mb-8">
                <p className="text-gray-600 text-center text-sm md:text-base leading-relaxed">
                  I'm here to help you navigate your child's developmental
                  journey with ease and confidence.
                </p>
                <p className="text-gray-600 text-center text-sm md:text-base leading-relaxed">
                  Think of me as your friendly guide, always ready to answer your
                  questions, track important milestones, and provide expert
                  advice whenever you need it.
                </p>
              </div>

              {/* Chat Button */}
              <button
                onClick={handleChatClick}
                disabled={isUpdating}
                className="w-full py-4 px-6 rounded-lg bg-[#1e3a5f] hover:bg-[#2d4a6f] transition-colors duration-200 text-white font-semibold text-base md:text-lg disabled:bg-gray-400"
              >
                {isUpdating ? "Loading Chat..." : "Let's Chat!"}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
